/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react'
import { isToday } from 'date-fns'

import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'

import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Color from '@tiptap/extension-color'
import Mention from '@tiptap/extension-mention'
import { HardBreak } from '@tiptap/extension-hard-break'

import { CustomPalette } from 'src/theme/palette'
import { useSearchQuery } from 'src/hooks/useSearchQuery'
import UserIcon from 'src/components/ui/UserIcon'
import LoadMoreTrigger from 'src/components/widgets/LoadMoreTrigger'
import {
  useNotificationsAction,
  useNotificationsSelect,
} from 'src/hooks/useNotifications'
import {
  ICommentContextNodeText,
  INotification,
} from 'src/services/notifications'
import Link from '@mui/material/Link/Link'
import { useRouterObjectPage } from 'src/hooks/router/useRouterObjectPage'
import { EditorContent, useEditor } from '@tiptap/react'
import { DocumentContent } from 'src/components/widgets/Document/DocumentContent'
import ScrollBar from 'react-perfect-scrollbar'
import Button from '@mui/material/Button'
import { useDateTime } from 'src/hooks/useDatetime'
import { useRouterMailPage } from 'src/hooks/router/useRouterMail'
import { useTranslation } from 'react-i18next'

interface INotificationsMenu {
  anchor: HTMLElement | null
  handleClose: () => void
}

function extractTextFromHtml(html: string) {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  return tempDiv.innerText
}

const NotificationsMenu = ({ anchor, handleClose }: INotificationsMenu) => {
  const { t } = useTranslation('common')
  const open = Boolean(anchor)
  const notifications = useNotificationsSelect.notifications()
  const unreadCount = useNotificationsSelect.notificationsCount() || 0
  const getNotifications = useNotificationsAction.getNotifications()
  const getNotificationsStatus =
    useNotificationsAction.getNotifications.status()
  const postNotificationsMarkAsRead =
    useNotificationsAction.postNotificationsMarkAsRead()
  const postNotificationsMarkAsReadStatus =
    useNotificationsAction.postNotificationsMarkAsRead.status()

  React.useEffect(() => {
    getNotifications({})
  }, [open])
  const isShowMore = notifications.nextCursor

  const isEmpty =
    notifications.items.length === 0 && !getNotificationsStatus.isClear

  const loading =
    notifications.items.length === 0 && getNotificationsStatus.isPending

  const handleLoadMore = () => {
    if (notifications.nextCursor && !getNotificationsStatus.isPending) {
      getNotifications({
        cursor: notifications.nextCursor,
      })
    }
  }

  const handleReadAll = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    postNotificationsMarkAsRead({ all: true })
  }

  return (
    <Menu
      anchorEl={anchor}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={open}
      id="notif-menu"
      onClose={handleClose}
    >
      <Stack
        direction="column"
        sx={{ width: 400, height: 486, position: 'relative' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 4,
            py: 2,
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            fontSize={11}
            fontWeight={600}
            color="text.secondary"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {unreadCount > 0
              ? t('unreadCount', { count: unreadCount })
              : t('allRead')}
          </Typography>
          <Button
            variant="text"
            color="info"
            size="small"
            disabled={
              unreadCount === 0 || postNotificationsMarkAsReadStatus.isPending
            }
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleReadAll}
            sx={{ minWidth: 0, px: 1.5, py: 1, fontSize: 12, fontWeight: 500 }}
          >
            {t('readAll')}
          </Button>
        </Stack>
        <ScrollBar options={{ suppressScrollX: true }}>
          {loading &&
            new Array(10)
              .fill(' ')
              .map((_, ind) => <NotificationLoader key={ind} />)}
          {!loading &&
            !isEmpty &&
            notifications.items.map((notification) => (
              <NotificationItem
                key={notification.createdAt}
                data={notification}
                handleClose={handleClose}
              />
            ))}
          {!loading && isEmpty && <EmptyState />}
          {isShowMore && (
            <LoadMoreTrigger fetchMoreHandler={handleLoadMore}>
              {new Array(3).fill(' ').map((_, ind) => (
                <NotificationLoader key={ind} />
              ))}
            </LoadMoreTrigger>
          )}
        </ScrollBar>
      </Stack>
    </Menu>
  )
}

function EmptyState() {
  return (
    <Stack
      sx={{ width: '100%', height: '100%' }}
      color="grey.900"
      direction="column"
      alignItems="center"
      justifyContent="center"
      rowGap={2}
    >
      <UserIcon
        icon="material-symbols:notifications"
        color="inherit"
        fontSize={30}
      />
      <Typography fontSize={12} fontWeight={500} color="grey.900">
        There are no notifications here yet
      </Typography>
    </Stack>
  )
}

const ActionsLabels = {
  assign: 'Assigned to {serviceEntityName}',
  unassign: 'Unassigned from {serviceEntityName}',
  comment_reply: 'Comment reply',
  comment_created: 'Comment created',
  comment_mention: 'Mention in a comment',
  comment_resolve: 'Comment resolved',
  email_received: 'Email received',
  broadcast: 'System notification',
}

const getActionLabel = (notification: INotification) => {
  const label = ActionsLabels[notification.action as keyof typeof ActionsLabels]

  if (!label) return notification.action

  return label.replaceAll('{serviceEntityName}', notification.serviceEntityName)
}

function NotificationItem(props: {
  data: INotification
  handleClose: () => void
}) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = React.useState(false)
  const [expanded, setExpanded] = React.useState(false)
  const { getLabel } = useDateTime()

  const { data, handleClose } = props
  const { setPathQuery } = useSearchQuery()
  const routeObjectPage = useRouterObjectPage()
  const routerMailPage = useRouterMailPage()

  const isUnread = !data.isRead
  const createdAt = new Date(data.createdAt)
  const createdAtLabel = getLabel(
    createdAt,
    isToday(createdAt) ? 'hh:mma' : 'hh:mma MM-dd'
  )

  const editor = useEditor({
    content: {
      type: 'doc',
      content: data.notificationContext.comment?.filter(
        (child) =>
          child.type !== 'text' || (child.text && child.text.trim() !== '')
      ),
    },
    editable: false,

    extensions: [
      HardBreak.configure({
        keepMarks: true,
      }),
      Document,
      Text,
      Color,
      Paragraph,

      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
      }),
    ],
  })

  const actionLabel = getActionLabel(data)

  const handleClick = () => {
    if (data.service === 'flow-forge' && data.action === 'broadcast') {
      const contextData = data.notificationContext.data

      if (contextData?.objectId) {
        const scheme = contextData.type || contextData.scheme
        if (scheme) {
          setPathQuery(`/storage/${scheme}/${contextData.objectId}`, {})
        }
      }

      handleClose()

      return
    }

    if (data.service === 'storage') {
      if (!data.storageObjectInfo) return

      if (data.storageObjectInfo.parentObject) {
        routeObjectPage.selectObjectChildrenPath(
          `/storage/${data.storageObjectInfo.parentObject.objectSchemeIdentifier}/${data.storageObjectInfo.parentObject.uniqId}`,
          {
            schemeName: data.storageObjectInfo.objectSchemeIdentifier,
            objectUniqId: data.storageObjectInfo.uniqId,
          }
        )
      } else {
        setPathQuery(
          `/storage/${data.storageObjectInfo.objectSchemeIdentifier}/${data.storageObjectInfo.uniqId}`,
          {}
        )
      }
    } else if (data.service === 'mail' && data.notificationContext.threadId) {
      routerMailPage.handleOpenThreadModal(
        String(data.notificationContext.threadId)
      )
    }

    handleClose()
  }

  React.useEffect(() => {
    if (contentRef.current) {
      const tiptapElement = contentRef.current.querySelector(
        '.tiptap'
      ) as HTMLElement
      if (tiptapElement) {
        const hasTextOverflow =
          tiptapElement.scrollWidth > tiptapElement.clientWidth
        setHasOverflow(hasTextOverflow)
      }
    }
  }, [editor])

  const handleShowMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setExpanded((prev) => !prev)
  }
  const { t } = useTranslation('common')

  const isBroadcast = data.action === 'broadcast'

  return (
    <MenuItem
      onClick={handleClick}
      sx={{ m: 0, borderRadius: 0, p: 4, minHeight: 95 }}
    >
      <Stack direction="row" sx={{ width: '100%' }} columnGap={3.3}>
        {isBroadcast ? (
          <Stack direction="row" justifyContent={'center'} mt={2}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: 16,
                backgroundColor: 'info.main',
              }}
            >
              <UserIcon icon="material-symbols:notifications" fontSize={20} />
            </Avatar>
          </Stack>
        ) : (
          data?.initiatorUser && (
            <Stack direction="row" justifyContent={'center'} mt={2}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 16,
                  border: '1px solid',
                  borderColor: 'grey.300',
                }}
                alt={data?.initiatorUser?.firstName}
                src={data?.initiatorUser?.avatar}
              />
            </Stack>
          )
        )}

        <Stack
          direction="column"
          flexBasis={0}
          flexGrow={1}
          flexShrink={1}
          sx={{ width: 0 }}
        >
          <Stack
            direction="row"
            justifyContent={'space-between'}
            alignItems="flex-start"
            flexBasis={0}
            flexGrow={1}
            flexShrink={1}
          >
            <Stack spacing={1} direction={'row'}>
              <Typography
                fontSize={14}
                fontWeight={isUnread ? 600 : 400}
                color="grey.900"
              >
                {isBroadcast
                  ? t('flowForge')
                  : `${data?.initiatorUser?.firstName} ${data?.initiatorUser?.lastName}`}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              columnGap={2}
              justifyContent="center"
            >
              <Typography fontSize={12} color="text.secondary" fontWeight={400}>
                {createdAtLabel}
              </Typography>
              {isUnread && (
                <Stack
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: (theme) =>
                      (theme.palette as CustomPalette).UI.text.info,
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Typography
            fontSize={12}
            color="info.main"
            fontWeight={isUnread ? 600 : 400}
          >
            {actionLabel}
          </Typography>

          {data.service === 'mail' && data.notificationContext.thread && (
            <Stack
              mt={1}
              direction="column"
              sx={{
                width: '100%',
              }}
            >
              <Typography
                fontSize={12}
                color={'grey[900]'}
                textOverflow="ellipsis"
                width={'100%'}
                overflow="hidden"
                fontWeight={600}
              >
                {data.notificationContext.thread.subject}
              </Typography>
              <Typography
                fontSize={12}
                color={'grey[900]'}
                textOverflow="ellipsis"
                width={'100%'}
                overflow="hidden"
              >
                {data.notificationContext.thread.lastEmail.info.snippet}
              </Typography>
            </Stack>
          )}

          {data.service === 'storage' && (
            <>
              {data.notificationContext.comment && (
                <Stack
                  mt={1.5}
                  ref={contentRef}
                  sx={{
                    '.text-style': {
                      '.tiptap': {
                        fontSize: 12,
                        color: 'text.secondary',

                        ...(expanded
                          ? {}
                          : {
                              whiteSpace: 'nowrap !important',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }),
                      },
                    },
                  }}
                >
                  <DocumentContent className="text-style" removePadding>
                    <EditorContent editor={editor} />
                  </DocumentContent>
                </Stack>
              )}
              {hasOverflow && (
                <Stack display="block" mt={1}>
                  <Button
                    variant="text"
                    onClick={handleShowMore}
                    color="success"
                    sx={{ p: 1.5 }}
                  >
                    <Typography fontSize={12} fontWeight={400} color="inherit">
                      {expanded ? t('showLess') : t('showMore')}
                    </Typography>
                  </Button>
                </Stack>
              )}
              {data.storageObjectInfo && (
                <Stack
                  direction={'row'}
                  spacing={1}
                  alignItems={'center'}
                  mt={2}
                >
                  <Avatar
                    src={data.storageObjectInfo?.icon}
                    sx={{ width: 20, height: 20 }}
                  >
                    {data.storageObjectInfo?.primary?.slice(0, 1).toUpperCase()}
                  </Avatar>

                  <Typography fontSize={12} color="grey.700" fontWeight={500}>
                    {data.storageObjectInfo?.primary}
                  </Typography>
                </Stack>
              )}
            </>
          )}

          {data.service === 'flow-forge' && isBroadcast && (
            <Stack mt={1.5} direction="column" spacing={1}>
              {data.notificationContext.title && (
                <Typography fontSize={14} fontWeight={600} color="grey.900">
                  {data.notificationContext.title}
                </Typography>
              )}
              {data.notificationContext.body && (
                <Typography
                  fontSize={12}
                  color="text.secondary"
                  sx={{
                    wordBreak: 'break-word',
                    ...(expanded
                      ? {
                          whiteSpace: 'normal',
                        }
                      : {
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }),
                  }}
                >
                  {data.notificationContext.body}
                </Typography>
              )}
              {data.notificationContext.body &&
                data.notificationContext.body.length > 100 && (
                  <Stack display="block">
                    <Button
                      variant="text"
                      onClick={handleShowMore}
                      color="success"
                      sx={{ p: 1.5 }}
                    >
                      <Typography
                        fontSize={12}
                        fontWeight={400}
                        color="inherit"
                      >
                        {expanded ? t('showLess') : t('showMore')}
                      </Typography>
                    </Button>
                  </Stack>
                )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </MenuItem>
  )
}

function NotificationLoader() {
  return (
    <MenuItem disabled>
      <Stack direction="row" columnGap={2} flex={1}>
        <Skeleton variant="circular" sx={{ width: 40, height: 40 }} />
        <Stack direction="column" flex={1}>
          <Skeleton variant="text" sx={{ width: 120, fontSize: 14 }} />
          <Skeleton variant="text" sx={{ width: 130, fontSize: 11 }} />
          <Skeleton variant="text" sx={{ width: 150, fontSize: 12 }} />
        </Stack>
      </Stack>
    </MenuItem>
  )
}

export default NotificationsMenu
